import { Directive, HostListener, ElementRef, Renderer2, Input } from '@angular/core';

/**
 * Auto-formats the F.E.I.N. # (Tax ID) input as the user types.
 *
 * US:      formats 9 digits as EIN  ->  XX-XXXXXXX (dash after the 2nd digit).
 * Canada:  keeps digits only (max 9, no dash) — the Canadian Business # must be
 *          exactly 9 digits with no separators, so masking is disabled there.
 *
 * The directive is country-aware via [feinCountry] so it never inserts a dash
 * that would break the Canadian format validation.
 */
@Directive({
    selector: '[appFeinMask]',
    standalone: false
})
export class FeinMaskDirective {

  @Input() feinCountry: string = '';

  // Guards against re-entrancy caused by re-dispatching the 'input' event below.
  private isProcessing = false;

  constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) { }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    if (this.isProcessing) { return; }

    const input = this.el.nativeElement as HTMLInputElement;
    const previousValue = input.value;
    const caret = input.selectionStart ?? previousValue.length;

    const digitsBeforeCaret = previousValue.slice(0, caret).replace(/[^\d]/g, '').length;

    const maskedValue = this.maskFein(previousValue);
    const newCaret = this.caretPositionForDigits(maskedValue, digitsBeforeCaret);

    this.isProcessing = true;
    this.renderer.setProperty(input, 'value', maskedValue);
    // Notify Angular's value accessor so the bound FormControl picks up the masked value.
    input.dispatchEvent(new Event('input'));
    this.isProcessing = false;

    try {
      input.setSelectionRange(newCaret, newCaret);
    } catch {
      // setSelectionRange is unsupported on some input types — safe to ignore.
    }
  }

  private caretPositionForDigits(masked: string, digitCount: number): number {
    if (digitCount <= 0) { return 0; }
    let seen = 0;
    for (let i = 0; i < masked.length; i++) {
      if (/\d/.test(masked[i])) {
        seen++;
        if (seen === digitCount) { return i + 1; }
      }
    }
    return masked.length;
  }

  private maskFein(value: string): string {
    const digits = value.replace(/[^\d]/g, '');

    // Canada Business # is exactly 9 digits with no separators — never insert a dash.
    if (this.feinCountry === 'Canada') {
      return digits.slice(0, 9);
    }

    // US EIN format: XX-XXXXXXX (dash after the 2nd digit), capped at 9 digits.
    const capped = digits.slice(0, 9);
    if (capped.length <= 2) {
      return capped;
    }
    return `${capped.slice(0, 2)}-${capped.slice(2)}`;
  }
}
